import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { ModalActions, ModalContent, ModalHeader } from '@vromo/ui/components/Modal';
import Title from '@vromo/ui/components/Title';
import Button from '@vromo/ui/components/Button';
import { css } from 'emotion';
import Input from '@vromo/ui/components/Input';
import { update } from 'serializr';
import { Blueprint, TaskBlueprint } from '@vromo/models';
import BlueprintTask from '@vromo/dashboard/src/blueprints/BlueprintTask';
import BlueprintTaskEditor from '@vromo/dashboard/src/blueprints/BlueprintTaskEditor';
import BlueprintTaskSources from '@vromo/dashboard/src/blueprints/BlueprintTaskSources';
import {
    AddButtonContainer,
    FilterRow,
    Flex,
    GreyDivider,
    TwoPageModal
} from '@vromo/dashboard/src/blueprints/BlueprintStyles';
import Alert from '@vromo/ui/components/Alert';
import first from 'lodash/first';
import { runInAction } from 'mobx';
import { useTranslation } from 'react-i18next';

type Step = 'step 1' | 'step 2';

type Props = {
    open: boolean;
    close: () => void;
    blueprint: Blueprint;
    onSave: (blueprint: Blueprint) => void;
};

export default observer(function NewBlueprintWizard({ close, blueprint, onSave }: Props) {
    const [step, setStep] = useState<Step | undefined>('step 1');
    const [selectedTask, setSelectedTask] = useState<number | null>(null);
    const [actionsCompleted, setActionsCompleted] = useState<boolean>(false);
    const [name, setName] = useState('');

    const [errors, setErrors] = useState<Array<any>>([]);

    const { t } = useTranslation();

    useEffect(() => {
        if (blueprint.template.tasks.length === 4) {
            setActionsCompleted(true);
        }
    }, [blueprint.template.tasks]);

    function removeTask(ix: number) {
        runInAction(() => update(blueprint, blueprint.template.tasks.splice(ix, 1)));
    }

    function addNewTask(newTask: TaskBlueprint) {
        runInAction(() => update(blueprint, { tasks: blueprint.template.tasks.push(newTask) }));
    }

    function proceedToStep2() {
        if (name.length === 0) {
            setErrors([
                {
                    field: 'name',
                    message: 'Please enter a name before continuing.'
                }
            ]);
        } else {
            runInAction(() => update(blueprint, {
                name: name
            }));

            setErrors([]);
            setStep('step 2');
        }
    }
    return (
        <>
            {step === 'step 1' ? (
                <>
                    <ModalHeader>
                        <Title>{t('blueprint.jobTemplate')}</Title>
                    </ModalHeader>
                    <ModalContent>
                        {errors.length !== 0 && <Alert danger>{first(errors).message}</Alert>}
                        <p>
                            {t('blueprint.jobTemplateDescription')}
                            <br />
                            {t('blueprint.followSteps')}
                        </p>
                        <div
                            className={css`
                                display: flex;
                                flex-direction: column;
                                flex: 1;
                                margin: 10px 0px;
                            `}>
                            <Input
                                label="Template Name"
                                // @ts-ignore
                                value={name}
                                onChange={(e: any) => setName(e.target.value)}
                                placeholder="4 Task Standard Template"
                            />
                        </div>
                    </ModalContent>
                    <ModalActions>
                        <Button onClick={close}>{t('blueprint.cancelButton')}</Button>
                        <Button primary onClick={proceedToStep2}>
                            {t('blueprint.nextButton')}
                        </Button>
                    </ModalActions>
                </>
            ) : step === 'step 2' ? (
                <>
                    {selectedTask !== null ? (
                        <>
                            <BlueprintTaskEditor
                                taskTemplate={
                                    selectedTask < blueprint.template.tasks.length
                                        ? blueprint.template.tasks[selectedTask]!
                                        : new TaskBlueprint()
                                }
                                cancel={() => setSelectedTask(null)}
                                isNew={selectedTask >= blueprint.template.tasks.length}
                                addTask={addNewTask}
                            />
                        </>
                    ) : (
                        <>
                            <ModalHeader>
                                <Title>{t('blueprint.jobTemplate')}</Title>
                            </ModalHeader>
                            <ModalContent>
                                <TwoPageModal>
                                    <Flex>
                                        <p>
                                            {t('blueprint.basicTemplateDescription')
                                                .split('*')
                                                .map((text, index) => 
                                                    index % 2 === 0 ? (
                                                        text
                                                    ) : (
                                                        <b>{text}</b>
                                                    ))
                                            }
                                        </p>
                                        <FilterRow>
                                            <label>{t('blueprint.inputTasks')}</label>
                                        </FilterRow>
                                        <FilterRow>
                                            <BlueprintTaskSources
                                                isNew={true}
                                                blueprint={blueprint}
                                            />
                                        </FilterRow>
                                    </Flex>
                                    <GreyDivider />
                                    <Flex>
                                        <FilterRow>
                                            <label>{t('blueprint.outputTasks')}</label>
                                        </FilterRow>
                                        {blueprint.template.tasks.length === 0 ? (
                                            <p>
                                                <i>{t('blueprint.noOutputTasks')}</i>
                                            </p>
                                        ) : (
                                            blueprint.template.tasks.map((t, ix) => (<BlueprintTask
                                                key={t.id}
                                                index={ix}
                                                taskTemplate={t}
                                                selectTask={() => setSelectedTask(ix)}
                                                removeTask={() => removeTask(ix)}
                                            />)
                                            )
                                        )}
                                        {actionsCompleted && (
                                            <div className={AddButtonContainer}>
                                                <Button
                                                    primary
                                                    onClick={() =>
                                                        setSelectedTask(
                                                            blueprint.template.tasks.length
                                                        )
                                                    }>
                                                    {t('blueprint.addTask')}
                                                </Button>
                                            </div>
                                        )}
                                    </Flex>
                                </TwoPageModal>
                            </ModalContent>
                            <ModalActions>
                                <Button onClick={() => setStep('step 1')}>{t('blueprint.previousButton')}</Button>
                                <Button
                                    primary
                                    onClick={() => {
                                        onSave(blueprint);
                                        close();
                                    }}>
                                    {t('blueprint.saveButton')}
                                </Button>
                            </ModalActions>
                        </>
                    )}
                </>
            ) : null}
        </>
    );
});
